import { DOCUMENT } from '@angular/common';
import { Component, DestroyRef, type OnInit, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressBar } from '@angular/material/progress-bar';
import { NavigationEnd, ResolveEnd, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbsStore } from '@sds/breadcrumbs';
import { LoadingBarStore } from '@sds/loading-bar';
import { LocaleStore, SharedStore } from '@sds/shared';
import { Language } from '@sds/shared/enums';
import { PageContextProviderService } from '@sds/shared/services';
import { filter } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { RouteTimingService } from './services/route-timing.service';
import { UserAnalyticsInitService } from './services/user-analytics-init.service';
import { ThemeHandlerComponent } from './theme-handler/theme-handler.component';

@Component({
  selector: 'sds-root',
  templateUrl: './app.component.html',
  imports: [ThemeHandlerComponent, MatProgressBar]
})
export class AppComponent implements OnInit {
  private readonly breadcrumbsStore = inject(BreadcrumbsStore);
  private readonly pageContextProvider = inject(PageContextProviderService);
  private readonly localeStore = inject(LocaleStore);
  private readonly sharedStore = inject(SharedStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly swUpdate = inject(SwUpdate);
  private readonly userAnalyticsInitService = inject(UserAnalyticsInitService);
  private readonly routeTimingService = inject(RouteTimingService);
  private readonly document = inject(DOCUMENT);
  loader = inject(LoadingBarStore);

  constructor() {
    effect(() => {
      this.translateService.addLangs(this.localeStore.languages());
    });
    this.userAnalyticsInitService.initialize(environment.production);
    this.routeTimingService.start();
  }

  ngOnInit(): void {
    const defaultLang = Language.EN_GB;
    this.translateService.setDefaultLang(defaultLang);

    this.router.events
      .pipe(
        filter((event): event is ResolveEnd => event instanceof ResolveEnd),
        filter(event => {
          const currentPathname = this.router.url.split('?')[0];
          const nextPathname = event.urlAfterRedirects.split('?')[0];
          return currentPathname !== nextPathname;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => {
        this.breadcrumbsStore.setBreadcrumbs([]);
        this.pageContextProvider.setPageContext(null);
        this.userAnalyticsInitService.trackNavigation(event.urlAfterRedirects);
      });

    this.swUpdate.versionUpdates
      .pipe(
        filter(event => event.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => {
        this.sharedStore.setAvailableUpdate(event);
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        filter(() => !!this.sharedStore.availableUpdate()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.document.location.reload();
      });
  }
}
